import React from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { DayOfWeek, FacultyTimetable } from "../../types";
import { DAY_LABELS, colorOf, todayDay } from "./timetableUi";

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50" };
const stickyColSx = { position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper", borderRight: "1px solid", borderRightColor: "divider" };

/**
 * One faculty's week, read-only: class + subject in each period they teach, "Free" otherwise.
 * Today's column is highlighted. Used by My Timetable (faculty) and Timetable → By Faculty (admin).
 */
export const FacultyWeekGrid: React.FC<{ timetable: FacultyTimetable }> = ({ timetable }) => {
  const today = todayDay();
  const { periods, days, slots } = timetable;
  const slotAt = (day: DayOfWeek, periodId: number) => slots.find((s) => s.day === day && s.periodId === periodId);
  const isToday = (day: DayOfWeek) => day === today;
  const todayBg = (theme: any) => alpha(theme.palette.primary.main, 0.04);

  if (slots.length === 0 || days.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, py: 7, px: 2, textAlign: "center" }}>
        <EventBusyOutlinedIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
        <Typography fontWeight={600}>No timetable yet</Typography>
        <Typography variant="body2" color="text.secondary">
          Periods appear here once the timetable is set in Academics → Timetable.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 140 + days.length * 130 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headCellSx, ...stickyColSx, bgcolor: "grey.50", width: 120 }}>Period</TableCell>
            {days.map((day) => (
              <TableCell
                key={day}
                align="center"
                sx={{ ...headCellSx, ...(isToday(day) && { bgcolor: (t: any) => alpha(t.palette.primary.main, 0.1), color: "primary.main" }) }}
              >
                {DAY_LABELS[day]}
                {isToday(day) && (
                  <Typography component="span" variant="caption" sx={{ display: "block", lineHeight: 1, fontWeight: 600 }}>
                    Today
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {periods.map((period) =>
            period.isBreak ? (
              <TableRow key={period.id}>
                <TableCell sx={{ ...stickyColSx, bgcolor: "grey.50", py: 0.5 }}>
                  <Typography variant="caption" fontStyle="italic" color="text.secondary">
                    {period.startTime}–{period.endTime}
                  </Typography>
                </TableCell>
                <TableCell colSpan={days.length} align="center" sx={{ bgcolor: "grey.50", py: 0.5 }}>
                  <Typography variant="caption" fontStyle="italic" color="text.secondary" letterSpacing="0.04em">
                    {period.name}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={period.id}>
                <TableCell sx={{ ...stickyColSx, py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {period.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {period.startTime}–{period.endTime}
                  </Typography>
                </TableCell>
                {days.map((day) => {
                  const slot = slotAt(day, period.id!);
                  const color = slot ? colorOf(slot.subjectCode) : undefined;
                  return (
                    <TableCell key={day} sx={{ p: 0.75, ...(isToday(day) && { bgcolor: todayBg }) }}>
                      {slot ? (
                        <Box sx={{ px: 1, py: 0.75, borderRadius: 1.5, bgcolor: alpha(color!, 0.08), borderLeft: `3px solid ${color}` }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ color }}>
                            {slot.subjectName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {slot.className}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "success.main", opacity: 0.8 }}>
                          Free
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** "24 periods / week · 4 classes" for the top bar. */
export const WeekSummary: React.FC<{ timetable: FacultyTimetable }> = ({ timetable }) => {
  const classes = new Set(timetable.slots.map((s) => s.gradeSectionId)).size;
  if (timetable.slots.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="baseline">
      <Typography variant="body2" fontWeight={700}>
        {timetable.slots.length}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        periods / week · {classes} class{classes === 1 ? "" : "es"}
      </Typography>
    </Stack>
  );
};
